import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  Image,
  Save,
  Upload,
  Trash2,
  Link2,
  Shield,
  Reply,
  Bot,
  Plane,
  Forward,
  Eye,
  Timer,
  Palette,
  Bell,
  CalendarClock,
  ShieldAlert,
  Newspaper,
} from 'lucide-react';
import { PageSkeleton } from './Skeleton';
import { useAuth } from '../context/AuthContext';
import { useMailPreferences } from '../context/MailPreferencesContext';
import { DEFAULT_MAIL_PREFERENCES, normalizeMailPreferences, isEmptyRichText, CALENDAR_REMINDER_OPTIONS } from '../utils/mailPreferences';
import {
  desktopNotificationsSupported,
  ensureMailNotificationSW,
  ensureWebPushSubscription,
  bootstrapDesktopNotifications,
  getDesktopNotificationDiagnostics,
  getDesktopNotificationPermission,
  notifyNewMail,
  requestDesktopNotificationPermission,
  sendServerPushTest,
  toastNewMail,
  webPushSupported,
} from '../utils/desktopNotifications';
import SpamRulesSettings from './SpamRulesSettings';
import SubscriptionSettings from './SubscriptionSettings';
import ThemePicker from './ThemePicker';
import { MediaPickerButton } from './MediaLibrarySettings';
import { textToHtmlLines, escapeHtml } from '../utils/emailDesign';
import { quillHtmlToEmailHtml } from '../utils/quillEmailHtml';
import {
  emailComposerFormats,
  emailComposerKeyboard,
  emailComposerToolbar,
  prepareEmailComposerQuill,
} from '../utils/quillToolbar';

const EMPTY_BANNER = {
  enabled: false,
  imageUrl: '',
  linkUrl: '',
  altText: '',
  signatureText: '',
  imageSize: 'medium',
  skipOnDesignTemplates: true,
};

const BANNER_IMAGE_SIZES = [
  { id: 'small', label: 'Klein', width: 120 },
  { id: 'medium', label: 'Mittel', width: 200 },
  { id: 'large', label: 'Groß', width: 320 },
];

const bannerImageWidth = (size) =>
  BANNER_IMAGE_SIZES.find((s) => s.id === size)?.width || 200;

const SIGNATURE_GREETING_PRESETS = [
  'Mit freundlichen Grüßen',
  'Viele Grüße',
  'Beste Grüße',
  'Herzliche Grüße',
];

/** Fertige, gesetzeskonforme Abschluss-Signatur (Angaben gem. § 5 DDG / Impressum bauos.de) */
const BAUOS_LEGAL_SIGNATURE = {
  id: 'bauos-legal',
  label: 'BauOS Signatur',
  html: [
    '<p>Mit freundlichen Grüßen</p>',
    '<p><br></p>',
    '<p><strong>NAME</strong></p>',
    '<p>Leiter Vertrieb</p>',
    '<p><br></p>',
    '<p><strong><a href="https://bauos.de" rel="noopener noreferrer">BauOS</a></strong> – Software für Baustelle, Lager &amp; Aufgabenplanung</p>',
    '<p>Inhaber: Nicolai Held</p>',
    '<p><br></p>',
    '<p>Bockenfelderstraße 232A</p>',
    '<p>44388 Dortmund</p>',
    '<p><br></p>',
    '<p>E-Mail: <a href="mailto:info@bauos.de" rel="noopener noreferrer">info@bauos.de</a></p>',
    '<p>Web: <a href="https://www.bauos.de" rel="noopener noreferrer">www.bauos.de</a></p>',
    '<p><br></p>',
    '<p>USt-IdNr.: DE366316777</p>',
  ].join(''),
};

const signatureLooksLikeHtml = (value) => /<[a-z][\s\S]*>/i.test(String(value || ''));

/** Gespeicherte Signatur für den Quill-Editor aufbereiten */
const signatureToEditorHtml = (content) => {
  const raw = String(content || '');
  if (!raw.trim()) return '';
  if (signatureLooksLikeHtml(raw)) return raw;
  const withLinks = textToHtmlLines(raw, '#2563eb');
  return withLinks
    .split(/<br\s*\/?>/i)
    .map((line) => `<p>${line || '<br>'}</p>`)
    .join('');
};

const SETTINGS_SECTIONS = [
  { id: 'design', label: 'Design', shortLabel: 'Design', icon: Palette },
  { id: 'banner', label: 'Abschluss & Banner', shortLabel: 'Banner', icon: Image },
  { id: 'privacy', label: 'Privatsphäre', shortLabel: 'Privat', icon: Shield },
  { id: 'mail', label: 'Mail-Verhalten', shortLabel: 'Mail', icon: Reply },
  { id: 'spam', label: 'Spam-Filter', shortLabel: 'Spam', icon: ShieldAlert },
  { id: 'subscriptions', label: 'Newsletter & Abos', shortLabel: 'Abos', icon: Newspaper },
  { id: 'automation', label: 'Automatisierung', shortLabel: 'Auto', icon: Bot },
];

/** Deep-Link-Hashes → Sektions-IDs (bestehende Links beibehalten) */
const HASH_TO_SECTION = {
  design: 'design',
  banner: 'banner',
  privacy: 'privacy',
  mail: 'mail',
  spam: 'spam',
  'spam-filter': 'spam',
  subscriptions: 'subscriptions',
  automation: 'automation',
};

const SECTION_TO_HASH = {
  design: 'design',
  banner: 'banner',
  privacy: 'privacy',
  mail: 'mail',
  spam: 'spam-filter',
  subscriptions: 'subscriptions',
  automation: 'automation',
};

const resolveSectionFromHash = () => {
  const raw = String(window.location.hash || '').replace(/^#/, '').trim();
  return HASH_TO_SECTION[raw] || 'design';
};

const SaveStatus = ({ status }) => {
  if (!status) return null;
  const label =
    status === 'saving' ? 'Speichern…' : status === 'error' ? 'Fehler' : 'Gespeichert';
  const color =
    status === 'error'
      ? 'text-red-600 dark:text-red-400'
      : status === 'saving'
        ? 'text-neutral-400'
        : 'text-emerald-600 dark:text-emerald-400';
  return <span className={`text-xs flex-shrink-0 tabular-nums ${color}`}>{label}</span>;
};

const SettingsToggle = ({ checked, onChange, label, description, disabled = false, compact = false }) => (
  <div
    className={`flex ${
      compact ? 'items-center gap-2.5' : 'items-start justify-between gap-3 sm:gap-4'
    } ${disabled ? 'opacity-50 pointer-events-none' : ''}`}
  >
    {label ? (
      <div className={`min-w-0 ${compact ? '' : 'flex-1'}`}>
        <div className="text-sm font-medium text-neutral-900 dark:text-neutral-100">{label}</div>
        {description ? (
          <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400 leading-relaxed">{description}</p>
        ) : null}
      </div>
    ) : null}
    <label className={`relative inline-flex cursor-pointer items-center flex-shrink-0 touch-manipulation ${compact || !label ? '' : 'mt-0.5'}`}>
      <input
        type="checkbox"
        className="peer sr-only"
        checked={!!checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="h-6 w-11 rounded-full bg-neutral-300 dark:bg-neutral-600 transition peer-checked:bg-primary-600 dark:peer-checked:bg-primary-500 peer-focus-visible:ring-2 peer-focus-visible:ring-primary-500 peer-focus-visible:ring-offset-2 dark:peer-focus-visible:ring-offset-neutral-900 after:absolute after:left-0.5 after:top-0.5 after:h-5 after:w-5 after:rounded-full after:bg-white dark:after:bg-neutral-900 after:shadow-sm after:transition after:content-[''] peer-checked:after:translate-x-5" />
    </label>
  </div>
);

const SettingsSection = ({ icon: Icon, title, description, actions, children, bodyClassName = 'space-y-5' }) => (
  <div className="card fade-in overflow-hidden">
    <div className="card-header">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            {Icon ? <Icon className="w-5 h-5 text-primary-600 dark:text-primary-400 flex-shrink-0" /> : null}
            <h3 className="text-base sm:text-lg font-medium text-neutral-900 dark:text-neutral-100 truncate">{title}</h3>
          </div>
          {description ? (
            <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400 leading-relaxed">{description}</p>
          ) : null}
        </div>
        {actions ? <div className="flex-shrink-0 self-start sm:self-auto">{actions}</div> : null}
      </div>
    </div>
    <div className={`card-body ${bodyClassName}`}>{children}</div>
  </div>
);

const Settings = () => {
  const { user } = useAuth();
  const location = useLocation();
  const userKey = String(user?.email || user?.id || '').trim().toLowerCase();
  const { preferences, save: saveMailPreferences } = useMailPreferences();
  const [loading, setLoading] = useState(true);
  const [banner, setBanner] = useState(EMPTY_BANNER);
  const [bannerImageError, setBannerImageError] = useState(false);
  const [savingBanner, setSavingBanner] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [mailPrefs, setMailPrefs] = useState(DEFAULT_MAIL_PREFERENCES);
  const [mailPrefsStatus, setMailPrefsStatus] = useState(''); // '' | 'saving' | 'saved' | 'error'
  const [trackingInfo, setTrackingInfo] = useState(null);
  const [desktopNotifPermission, setDesktopNotifPermission] = useState(() =>
    getDesktopNotificationPermission()
  );
  const mailPrefsRef = useRef(mailPrefs);
  const mailSaveTimerRef = useRef(null);
  const mailStatusTimerRef = useRef(null);
  const signatureQuillRef = useRef(null);
  const [signatureEditorKey, setSignatureEditorKey] = useState(0);
  const [activeSection, setActiveSection] = useState(resolveSectionFromHash);
  const mobileNavRef = useRef(null);
  const mobilePillRefs = useRef({});

  const signatureQuillModules = useMemo(
    () => ({
      toolbar: emailComposerToolbar,
      keyboard: emailComposerKeyboard,
    }),
    []
  );
  const signatureQuillFormats = useMemo(() => emailComposerFormats, []);

  const selectSection = useCallback((id) => {
    setActiveSection(id);
    const hash = SECTION_TO_HASH[id] || id;
    const next = `#${hash}`;
    if (window.location.hash !== next) {
      window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}${next}`);
    }
    // Auf Mobile nach Sektionswechsel wieder zum Panel-Anfang
    if (typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches) {
      window.requestAnimationFrame(() => {
        document.querySelector('.settings-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, []);

  useEffect(() => {
    const syncFromHash = () => setActiveSection(resolveSectionFromHash());
    syncFromHash();
    window.addEventListener('hashchange', syncFromHash);
    return () => window.removeEventListener('hashchange', syncFromHash);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    const el = mobilePillRefs.current[activeSection];
    if (!el || !mobileNavRef.current) return;
    el.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }, [activeSection]);

  useEffect(() => {
    mailPrefsRef.current = mailPrefs;
  }, [mailPrefs]);

  useEffect(() => {
    setDesktopNotifPermission(getDesktopNotificationPermission());
    const onVis = () => setDesktopNotifPermission(getDesktopNotificationPermission());
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  const desktopNotifDiag = getDesktopNotificationDiagnostics();
  const trackingHttpsBase = String(trackingInfo?.resolvedBaseUrl || '')
    .trim()
    .replace(/\/+$/, '');
  const desktopNotifHttpsHint = /^https:\/\//i.test(trackingHttpsBase)
    ? trackingHttpsBase
    : 'https://odium-services.de';

  const desktopNotifStatusLabel = (() => {
    if (!desktopNotifDiag.secure) return 'Nur mit HTTPS';
    if (!desktopNotifDiag.hasNotif || desktopNotifPermission === 'unsupported') {
      return 'Nicht unterstützt';
    }
    if (desktopNotifPermission === 'granted') return 'Erlaubt';
    if (desktopNotifPermission === 'denied') return 'Blockiert';
    return 'Noch nicht gefragt';
  })();

  useEffect(() => {
    setLoading(true);
    setBanner(EMPTY_BANNER);
    setBannerImageError(false);
    loadBanner();
    axios
      .get('/api/mail-preferences')
      .then((res) => {
        if (res.data?.tracking) setTrackingInfo(res.data.tracking);
      })
      .catch(() => setTrackingInfo(null));
    return () => {
      if (mailSaveTimerRef.current) clearTimeout(mailSaveTimerRef.current);
      if (mailStatusTimerRef.current) clearTimeout(mailStatusTimerRef.current);
    };
  }, [userKey]);

  // Preferences aus dem Context übernehmen (auch nach User-Wechsel)
  useEffect(() => {
    setMailPrefs(normalizeMailPreferences(preferences));
  }, [preferences, userKey]);

  const loadBanner = async () => {
    try {
      const res = await axios.get('/api/email-banner');
      if (res.data?.success && res.data.config) {
        const cfg = res.data.config;
        setBanner({
          ...EMPTY_BANNER,
          ...cfg,
          signatureText: signatureToEditorHtml(cfg.signatureText || cfg.footerText || ''),
        });
        setSignatureEditorKey((k) => k + 1);
      }
    } catch (_) {
    } finally {
      setLoading(false);
    }
  };

  const persistMailPrefs = useCallback(async (nextPrefs, { quiet = false } = {}) => {
    const normalized = normalizeMailPreferences(nextPrefs);
    try {
      setMailPrefsStatus('saving');
      await saveMailPreferences(normalized);
      setMailPrefsStatus('saved');
      if (!quiet) {
        // kurze Bestätigung, kein Toast-Spam bei jedem Klick
      }
      if (mailStatusTimerRef.current) clearTimeout(mailStatusTimerRef.current);
      mailStatusTimerRef.current = setTimeout(() => setMailPrefsStatus(''), 1600);
    } catch (error) {
      setMailPrefsStatus('error');
      toast.error(error.response?.data?.error || 'Einstellungen konnten nicht gespeichert werden');
      if (mailStatusTimerRef.current) clearTimeout(mailStatusTimerRef.current);
      mailStatusTimerRef.current = setTimeout(() => setMailPrefsStatus(''), 2500);
    }
  }, [saveMailPreferences]);

  const updateMailPrefs = (patch, { debounceMs = 0 } = {}) => {
    setMailPrefs((prev) => {
      const next = normalizeMailPreferences({ ...prev, ...patch });
      mailPrefsRef.current = next;
      if (mailSaveTimerRef.current) clearTimeout(mailSaveTimerRef.current);
      if (debounceMs > 0) {
        mailSaveTimerRef.current = setTimeout(() => {
          persistMailPrefs(mailPrefsRef.current, { quiet: true });
        }, debounceMs);
      } else {
        persistMailPrefs(next, { quiet: true });
      }
      return next;
    });
  };

  const handleRequestDesktopNotifPermission = async () => {
    const result = await requestDesktopNotificationPermission();
    setDesktopNotifPermission(result);
    if (result === 'granted') {
      await ensureMailNotificationSW();
      const push = await ensureWebPushSubscription();
      if (push.ok) {
        toast.success('Benachrichtigungen erlaubt — Hintergrund-Push aktiv');
      } else {
        toast.success('Desktop-Benachrichtigungen erlaubt');
      }
      if (!mailPrefsRef.current.desktopNotifications) {
        updateMailPrefs({ desktopNotifications: true });
      }
    } else if (result === 'denied') {
      toast.error('Benachrichtigungen wurden blockiert — bitte in den Browser-Einstellungen erlauben');
    } else if (result === 'unsupported') {
      const diag = getDesktopNotificationDiagnostics();
      toast.error(
        !diag.secure
          ? 'Benachrichtigungen brauchen HTTPS (oder localhost)'
          : 'Dieser Browser unterstützt keine Desktop-Benachrichtigungen'
      );
    }
  };

  const handleTestDesktopNotification = async () => {
    const diag = getDesktopNotificationDiagnostics();
    if (!diag.secure) {
      toast.error('Kein sicherer Kontext (HTTPS/localhost) — Benachrichtigungen sind blockiert');
      return;
    }
    let perm = getDesktopNotificationPermission();
    if (perm === 'default') {
      perm = await requestDesktopNotificationPermission();
      setDesktopNotifPermission(perm);
    } else {
      setDesktopNotifPermission(perm);
    }
    if (perm !== 'granted') {
      toast.error(
        perm === 'denied'
          ? 'Benachrichtigungen sind blockiert — bitte in den Browser-Einstellungen erlauben'
          : 'Bitte zuerst die Browser-Berechtigung erteilen'
      );
      return;
    }
    await ensureMailNotificationSW();
    await bootstrapDesktopNotifications({ enabled: true });

    const sample = {
      count: 1,
      from: 'Odium <noreply@odium.local>',
      subject: 'Willkommen — so sehen neue Mails aus',
    };

    toast('Test in 2 Sekunden…', { duration: 2000, id: 'odium-notif-test-wait' });
    await new Promise((r) => setTimeout(r, 2000));

    // 1) In-App-Toast (sichtbarer Tab)
    toastNewMail(sample);

    // 2) Push-Abo sicherstellen + Server-Push
    let pushOk = false;
    if (webPushSupported()) {
      const pushReady = await ensureWebPushSubscription();
      if (!pushReady.ok) {
        toast.error(
          pushReady.error ||
            'Push-Abo konnte nicht angelegt werden — lokaler Desktop-Test folgt'
        );
      } else {
        const pushResult = await sendServerPushTest();
        if (pushResult.ok) {
          pushOk = true;
          toast.success('Hintergrund-Push gesendet');
        } else {
          toast.error(
            pushResult.error ||
              'Hintergrund-Push fehlgeschlagen — lokaler Desktop-Test folgt'
          );
        }
      }
    }

    // 3) Lokaler Desktop-Toast (force — auch bei fokussiertem Fenster)
    const ok = await notifyNewMail({
      ...sample,
      enabled: true,
      force: true,
    });
    if (ok) {
      toast.success('Desktop-Benachrichtigung ausgelöst');
    } else if (!pushOk) {
      toast.error(
        'Desktop-Benachrichtigung blockiert — Windows: System → Benachrichtigungen → Browser erlauben'
      );
    }
  };

  const updateNestedMailPrefs = (key, patch, options) => {
    setMailPrefs((prev) => {
      const next = normalizeMailPreferences({
        ...prev,
        [key]: { ...(prev[key] || {}), ...patch },
      });
      mailPrefsRef.current = next;
      const debounceMs = options?.debounceMs || 0;
      if (mailSaveTimerRef.current) clearTimeout(mailSaveTimerRef.current);
      if (debounceMs > 0) {
        mailSaveTimerRef.current = setTimeout(() => {
          persistMailPrefs(mailPrefsRef.current, { quiet: true });
        }, debounceMs);
      } else {
        persistMailPrefs(next, { quiet: true });
      }
      return next;
    });
  };

  const hasBannerContent = !!(
    (banner.signatureText && !isEmptyRichText(banner.signatureText)) ||
    banner.imageUrl
  );

  useEffect(() => {
    setBannerImageError(false);
  }, [banner.imageUrl]);

  const saveBanner = async () => {
    try {
      if (banner.enabled && !hasBannerContent) {
        toast.error('Bitte Abschluss-Text und/oder ein Bild angeben');
        return;
      }
      setSavingBanner(true);
      const rawSig = String(banner.signatureText || '');
      const signatureHtml = isEmptyRichText(rawSig) ? '' : quillHtmlToEmailHtml(rawSig);
      // Data-URLs nicht mitsenden — Bild liegt nach Upload schon in email_banner_image
      const configToSave = {
        ...banner,
        signatureText: signatureHtml,
        imageUrl: String(banner.imageUrl || '').startsWith('data:image/')
          ? 'asset:local'
          : banner.imageUrl,
      };
      const res = await axios.put('/api/email-banner', { config: configToSave });
      if (res.data?.config) {
        setBanner({
          ...EMPTY_BANNER,
          ...res.data.config,
          signatureText: signatureToEditorHtml(res.data.config.signatureText || ''),
        });
        setSignatureEditorKey((k) => k + 1);
      }
      toast.success('E-Mail-Banner gespeichert');
    } catch (error) {
      const status = error.response?.status;
      const msg =
        error.response?.data?.error ||
        (status === 413
          ? 'Banner-Bild ist zu groß'
          : error.code === 'ERR_NETWORK'
            ? 'Server nicht erreichbar — bitte erneut versuchen'
            : 'Banner konnte nicht gespeichert werden');
      toast.error(msg);
    } finally {
      setSavingBanner(false);
    }
  };

  const handleBannerFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      setUploadingBanner(true);
      const formData = new FormData();
      formData.append('image', file);
      const res = await axios.post('/api/email-banner/upload', formData);
      if (res.data?.success && res.data.imageUrl) {
        setBanner((prev) => ({
          ...prev,
          enabled: true,
          imageUrl: res.data.imageUrl,
          altText: prev.altText || file.name.replace(/\.[^.]+$/, ''),
        }));
        toast.success('Bild hochgeladen');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Upload fehlgeschlagen');
    } finally {
      setUploadingBanner(false);
    }
  };

  const clearBannerImage = () => {
    setBanner((prev) => ({ ...prev, imageUrl: '' }));
  };

  const applySignatureGreeting = (preset) => {
    const safe = escapeHtml(preset);
    setBanner((prev) => {
      const current = String(prev.signatureText || '');
      const plain = String(current)
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      const plainFirst = plain.split(/\s+/).slice(0, 4).join(' ');
      const isBauosLegal =
        /BauOS/i.test(plain) && /USt-IdNr\.?\s*DE366316777/i.test(plain);
      const greetingOnly =
        !current.trim() ||
        isEmptyRichText(current) ||
        SIGNATURE_GREETING_PRESETS.some((p) => plainFirst === p || plainFirst.startsWith(p));
      let next;
      if (isBauosLegal) {
        // Nur Grußformel am Anfang der fertigen BauOS-Signatur tauschen
        next = String(BAUOS_LEGAL_SIGNATURE.html).replace(
          /<p>[^<]*Grüße[^<]*<\/p>/i,
          `<p>${safe}</p>`
        );
      } else if (greetingOnly) {
        next = `<p>${safe}</p>`;
      } else {
        next = `<p>${safe}</p>${current}`;
      }
      return { ...prev, enabled: true, signatureText: next };
    });
    setSignatureEditorKey((k) => k + 1);
  };

  const applyBauosLegalSignature = () => {
    setBanner((prev) => ({
      ...prev,
      enabled: true,
      signatureText: BAUOS_LEGAL_SIGNATURE.html,
    }));
    setSignatureEditorKey((k) => k + 1);
  };

  const handleSignatureQuillChange = (val, _delta, source) => {
    if (source !== 'user') return;
    setBanner((prev) => ({
      ...prev,
      enabled: true,
      signatureText: isEmptyRichText(val) ? '' : val,
    }));
  };

  const bannerSignatureHtml = () => {
    const raw = String(banner.signatureText || '');
    if (isEmptyRichText(raw)) return '';
    if (signatureLooksLikeHtml(raw)) return quillHtmlToEmailHtml(raw);
    return textToHtmlLines(raw, '#2563eb');
  };

  const renderBannerImage = (className = 'h-auto block border-0', { constrainPreview = false } = {}) => {
    if (!banner.imageUrl) return null;
    if (bannerImageError) {
      return (
        <div className="rounded-lg border border-dashed border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 px-3 py-3 text-xs text-amber-800 dark:text-amber-200">
          Bild konnte nicht geladen werden. Prüfe die URL oder nutze den Upload.
        </div>
      );
    }
    const width = bannerImageWidth(banner.imageSize);
    const img = (
      <img
        src={banner.imageUrl}
        alt={banner.altText || 'Banner'}
        width={width}
        referrerPolicy="no-referrer"
        className={className}
        style={{
          width: constrainPreview ? '100%' : `${width}px`,
          maxWidth: `${width}px`,
          height: 'auto',
          display: 'inline-block',
        }}
        onError={() => setBannerImageError(true)}
        onLoad={() => setBannerImageError(false)}
      />
    );
    if (banner.linkUrl?.trim()) {
      return (
        <a href={banner.linkUrl.trim()} target="_blank" rel="noopener noreferrer" className="inline-block no-underline">
          {img}
        </a>
      );
    }
    return img;
  };

  if (loading) {
    return <PageSkeleton variant="settings" />;
  }

  const navItems = SETTINGS_SECTIONS.map((section) => {
    const Icon = section.icon;
    const isActive = activeSection === section.id;
    return { ...section, Icon, isActive };
  });

  return (
    <div className="space-y-4 sm:space-y-6 pb-[max(1rem,env(safe-area-inset-bottom))]">
      <div className="fade-in flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 sm:gap-3">
        <div className="min-w-0">
          <h1 className="page-title">Einstellungen</h1>
          <p className="page-subtitle">
            <span className="sm:hidden">Design, Banner, Spam, Abos &amp; mehr</span>
            <span className="hidden sm:inline">
              Design, Banner, Datenschutz, Spam-Filter, Antworten und Automatisierung
            </span>
          </p>
        </div>
        <SaveStatus status={mailPrefsStatus} />
      </div>

      {/* Mobile: alle Bereiche sichtbar (Grid, kein Abschneiden) */}
      <nav
        ref={mobileNavRef}
        className="settings-nav-mobile lg:hidden fade-in"
        aria-label="Einstellungsbereiche"
      >
        <div className="settings-nav-pills">
          {navItems.map(({ id, label, shortLabel, Icon, isActive }) => (
            <button
              key={id}
              type="button"
              ref={(el) => {
                mobilePillRefs.current[id] = el;
              }}
              onClick={() => selectSection(id)}
              className={`settings-nav-pill ${isActive ? 'is-active' : ''}`}
              aria-current={isActive ? 'page' : undefined}
              title={label}
            >
              <Icon aria-hidden />
              <span>{shortLabel || label}</span>
            </button>
          ))}
        </div>
      </nav>

      <div className="settings-layout fade-in">
        {/* Desktop: sticky side nav */}
        <aside className="settings-nav-desktop" aria-label="Einstellungsbereiche">
          <nav className="settings-nav-list">
            {navItems.map(({ id, label, Icon, isActive }) => (
              <button
                key={id}
                type="button"
                onClick={() => selectSection(id)}
                className={`settings-nav-item ${isActive ? 'is-active' : ''}`}
                aria-current={isActive ? 'page' : undefined}
              >
                <Icon className="w-4 h-4 flex-shrink-0 opacity-80" />
                <span className="truncate">{label}</span>
              </button>
            ))}
          </nav>
        </aside>

        <div className="settings-panel">
          {activeSection === 'design' && (
            <SettingsSection
              icon={Palette}
              title="Design & Themes"
              description="Mehrere Dark- und Light-Themes — Standard ist Mitternacht."
              bodyClassName=""
            >
              <ThemePicker showHeader={false} />
            </SettingsSection>
          )}

          {activeSection === 'banner' && (
            <SettingsSection
              icon={Image}
              title="Abschluss & Banner"
              description="Grußformel, Signatur und optionales Logo — wird an ausgehende E-Mails angehängt."
              actions={
                <SettingsToggle
                  compact
                  checked={!!banner.enabled}
                  onChange={(enabled) => setBanner((prev) => ({ ...prev, enabled }))}
                  label="Aktiv"
                />
              }
            >
              <div className={`space-y-5 ${!banner.enabled ? 'opacity-60 pointer-events-none' : ''}`}>
                <SettingsToggle
                  checked={banner.skipOnDesignTemplates !== false}
                  onChange={(skipOnDesignTemplates) =>
                    setBanner((prev) => ({ ...prev, skipOnDesignTemplates }))
                  }
                  label="Bei Design-Vorlagen weglassen"
                  description="Design-Mails enthalten oft schon Footer oder Impressum — dann keine zusätzliche Signatur anhängen."
                />
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1.5">
                    Abschluss & Signatur
                  </label>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-2">
                    Fertige BauOS-Signatur mit Impressumsangaben vorwählen oder nur die Grußformel
                    setzen — wird unter jeder ausgehenden Mail angehängt
                    {banner.skipOnDesignTemplates !== false ? ' (außer bei Design-Vorlagen)' : ''}.
                  </p>
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <button
                      type="button"
                      onClick={applyBauosLegalSignature}
                      className="text-xs px-2.5 py-1 rounded-lg border border-primary-300 bg-primary-50 text-primary-800 dark:border-primary-700 dark:bg-primary-950/40 dark:text-primary-200 hover:bg-primary-100 dark:hover:bg-primary-900/50 font-medium"
                      title="Fertige Signatur mit Impressumsangaben (§ 5 DDG)"
                    >
                      {BAUOS_LEGAL_SIGNATURE.label}
                    </button>
                    {SIGNATURE_GREETING_PRESETS.map((preset) => (
                      <button
                        key={preset}
                        type="button"
                        onClick={() => applySignatureGreeting(preset)}
                        className="text-xs px-2.5 py-1 rounded-lg border border-neutral-200 dark:border-neutral-600 text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800"
                      >
                        {preset}
                      </button>
                    ))}
                  </div>
                  <div className="quill-container email-message-input overflow-hidden">
                    <ReactQuill
                      key={signatureEditorKey}
                      ref={(instance) => {
                        signatureQuillRef.current = instance;
                        if (instance) {
                          window.requestAnimationFrame(() => {
                            try {
                              prepareEmailComposerQuill(instance.getEditor?.());
                            } catch (_) {
                              /* ignore */
                            }
                          });
                        }
                      }}
                      theme="snow"
                      defaultValue={banner.signatureText || '<p><br></p>'}
                      onChange={handleSignatureQuillChange}
                      modules={signatureQuillModules}
                      formats={signatureQuillFormats}
                      placeholder="Mit freundlichen Grüßen…"
                      className="signature-quill min-h-[160px]"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                        Logo / Bild <span className="font-normal text-neutral-400">(optional)</span>
                      </label>
                      {banner.imageUrl ? (
                        <div className="rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-3">
                          {renderBannerImage('object-contain rounded-lg', { constrainPreview: true })}
                          <div className="mt-3 flex flex-wrap gap-2">
                            <label className="btn-secondary text-xs inline-flex items-center cursor-pointer">
                              <Upload className="w-3.5 h-3.5 mr-1.5" />
                              {uploadingBanner ? 'Lädt…' : 'Anderes Bild'}
                              <input
                                type="file"
                                accept="image/jpeg,image/png,image/gif,image/webp"
                                className="hidden"
                                disabled={uploadingBanner}
                                onChange={handleBannerFile}
                              />
                            </label>
                            <MediaPickerButton
                              label="Bibliothek"
                              onSelect={(url) =>
                                setBanner((prev) => ({
                                  ...prev,
                                  enabled: true,
                                  imageUrl: url,
                                }))
                              }
                            />
                            <button
                              type="button"
                              onClick={clearBannerImage}
                              className="btn-secondary text-xs inline-flex items-center text-red-600"
                            >
                              <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                              Entfernen
                            </button>
                          </div>
                        </div>
                      ) : (
                        <label className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-neutral-300 dark:border-neutral-600 bg-neutral-50 dark:bg-neutral-800/40 px-4 py-8 cursor-pointer hover:border-primary-400 transition-colors">
                          <Upload className="w-6 h-6 text-neutral-400" />
                          <span className="text-sm text-neutral-600 dark:text-neutral-300">
                            {uploadingBanner ? 'Bild wird hochgeladen…' : 'Bild hochladen'}
                          </span>
                          <span className="text-xs text-neutral-400">JPG, PNG, GIF oder WebP · max. 2 MB</span>
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/gif,image/webp"
                            className="hidden"
                            disabled={uploadingBanner}
                            onChange={handleBannerFile}
                          />
                        </label>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1.5">
                        Bild-URL <span className="font-normal text-neutral-400">(optional)</span>
                      </label>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <input
                          type="url"
                          value={banner.imageUrl.startsWith('data:') ? '' : banner.imageUrl}
                          onChange={(e) =>
                            setBanner((prev) => ({
                              ...prev,
                              enabled: true,
                              imageUrl: e.target.value,
                            }))
                          }
                          onBlur={(e) =>
                            setBanner((prev) => ({
                              ...prev,
                              imageUrl: e.target.value.trim(),
                            }))
                          }
                          className="input-field flex-1"
                          placeholder="https://… oder aus Bibliothek"
                          disabled={banner.imageUrl.startsWith('data:')}
                        />
                        <MediaPickerButton
                          label="Bibliothek"
                          onSelect={(url) =>
                            setBanner((prev) => ({
                              ...prev,
                              enabled: true,
                              imageUrl: url,
                            }))
                          }
                        />
                      </div>
                      {banner.imageUrl.startsWith('data:') && (
                        <p className="mt-1 text-xs text-neutral-400">
                          Hochgeladenes Bild aktiv — zum Nutzen einer URL zuerst entfernen.
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                        Bildgröße
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {BANNER_IMAGE_SIZES.map((size) => {
                          const active = (banner.imageSize || 'medium') === size.id;
                          return (
                            <button
                              key={size.id}
                              type="button"
                              onClick={() => setBanner((prev) => ({ ...prev, imageSize: size.id }))}
                              className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${
                                active
                                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-200'
                                  : 'border-neutral-200 dark:border-neutral-600 text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800'
                              }`}
                            >
                              {size.label}
                            </button>
                          );
                        })}
                      </div>
                      <p className="mt-1.5 text-xs text-neutral-400">
                        Breite in der E-Mail · Höhe bleibt proportional · auf Mobil max. 100&nbsp;%
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1.5 flex items-center gap-1.5">
                        <Link2 className="w-3.5 h-3.5" />
                        Link (optional)
                      </label>
                      <input
                        type="url"
                        value={banner.linkUrl}
                        onChange={(e) => setBanner((prev) => ({ ...prev, linkUrl: e.target.value }))}
                        className="input-field"
                        placeholder="https://…"
                      />
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                        Vorschau
                      </label>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${
                          banner.enabled && hasBannerContent
                            ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200'
                            : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300'
                        }`}
                      >
                        {banner.enabled && hasBannerContent ? 'Wird mitgesendet' : 'Noch nicht aktiv'}
                      </span>
                    </div>
                    <div className="rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-3 sm:p-4 min-h-[180px] sm:min-h-[220px] overflow-x-hidden">
                      <div className="text-sm text-neutral-800 dark:text-neutral-100 space-y-3 min-w-0 break-words">
                        <p className="text-neutral-400 dark:text-neutral-500 italic text-xs">
                          hier steht der eigentliche E-Mail-Text Ihrer Vorlage. Abschluss und Banner erscheinen
                          darunter — genau so wie beim Versand.
                        </p>
                        <hr className="border-neutral-200 dark:border-neutral-700" />
                        {hasBannerContent ? (
                          <div>
                            {banner.signatureText && !isEmptyRichText(banner.signatureText) ? (
                              <div
                                className="text-sm leading-relaxed text-neutral-800 dark:text-neutral-100"
                                dangerouslySetInnerHTML={{ __html: bannerSignatureHtml() }}
                              />
                            ) : null}
                            {banner.imageUrl ? (
                              <div
                                className={
                                  banner.signatureText && !isEmptyRichText(banner.signatureText)
                                    ? 'mt-4'
                                    : ''
                                }
                              >
                                {renderBannerImage('rounded-sm')}
                              </div>
                            ) : null}
                          </div>
                        ) : (
                          <p className="text-xs text-neutral-400">Noch kein Abschluss oder Bild hinterlegt.</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-stretch sm:justify-end pt-1">
                <button
                  type="button"
                  onClick={saveBanner}
                  disabled={savingBanner}
                  className="btn-primary flex items-center justify-center w-full sm:w-auto !py-2.5 sm:!py-2"
                >
                  {savingBanner ? (
                    <>
                      <div className="spinner-sm mr-2"></div>
                      Speichern...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Banner speichern
                    </>
                  )}
                </button>
              </div>
            </SettingsSection>
          )}

          {activeSection === 'privacy' && (
            <SettingsSection
              icon={Shield}
              title="Privatsphäre"
              description="Tracker, Benachrichtigungen, Termin-Erinnerungen und Öffnungs-Tracking an einem Ort."
              bodyClassName="space-y-0 divide-y divide-neutral-200 dark:divide-neutral-700"
            >
              <div className="pb-5 space-y-4">
                <div className="flex items-center gap-2 text-sm font-medium text-neutral-900 dark:text-neutral-100">
                  <Shield className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                  Tracker blockieren
                </div>
                <SettingsToggle
                  checked={!!mailPrefs.blockExternalContent}
                  onChange={(blockExternalContent) => updateMailPrefs({ blockExternalContent })}
                  label="Externe Bilder standardmäßig blockieren"
                  description="Nachladen von externen Inhalten in empfangenen Mails blockieren. In der Inbox können Bilder weiterhin manuell freigegeben werden."
                />
              </div>

              <div className="py-5 space-y-4">
                <div className="flex items-center gap-2 text-sm font-medium text-neutral-900 dark:text-neutral-100">
                  <Bell className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                  Benachrichtigungen
                </div>
                <SettingsToggle
                  checked={!!mailPrefs.desktopNotifications}
                  onChange={(desktopNotifications) => updateMailPrefs({ desktopNotifications })}
                  label="Push-Benachrichtigungen"
                  description="Neue Mails als System-Benachrichtigung — auch im Hintergrund (Windows/Android). iPhone: App zum Home-Bildschirm hinzufügen."
                />
                <div className="flex flex-wrap items-center gap-2.5">
                  <span
                    className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-medium tracking-wide ${
                      !desktopNotifDiag.secure
                        ? 'bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200'
                        : desktopNotifPermission === 'granted'
                          ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300'
                          : desktopNotifPermission === 'denied'
                            ? 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300'
                            : 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300'
                    }`}
                  >
                    {desktopNotifStatusLabel}
                  </span>
                  {desktopNotificationsSupported() && desktopNotifPermission !== 'granted' ? (
                    <button
                      type="button"
                      onClick={handleRequestDesktopNotifPermission}
                      disabled={desktopNotifPermission === 'denied'}
                      className="inline-flex items-center gap-1.5 rounded-full border border-neutral-300 dark:border-neutral-600 px-3 py-1.5 text-xs font-medium text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Berechtigung erteilen
                    </button>
                  ) : null}
                  {desktopNotificationsSupported() ? (
                    <button
                      type="button"
                      onClick={handleTestDesktopNotification}
                      disabled={desktopNotifPermission === 'denied' || desktopNotifPermission === 'unsupported'}
                      className="inline-flex items-center gap-1.5 rounded-full bg-neutral-900 dark:bg-neutral-100 px-3 py-1.5 text-xs font-medium text-neutral-50 dark:text-neutral-900 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Testen
                    </button>
                  ) : null}
                </div>
                {!desktopNotifDiag.secure ? (
                  <p className="text-xs text-amber-800/90 dark:text-amber-200/80 leading-relaxed">
                    Browser erlauben Push nur über HTTPS (localhost ausgenommen). Du bist
                    gerade über HTTP verbunden — öffne die App unter{' '}
                    <a
                      href={desktopNotifHttpsHint}
                      className="underline font-medium break-all"
                      rel="noreferrer"
                    >
                      {desktopNotifHttpsHint}
                    </a>
                    .
                  </p>
                ) : desktopNotifPermission === 'denied' ? (
                  <p className="text-xs text-amber-800/90 dark:text-amber-200/80 leading-relaxed">
                    Blockiert — Schloss in der Adressleiste → Benachrichtigungen zulassen.
                  </p>
                ) : (
                  <p className="text-xs text-neutral-500 dark:text-neutral-500 leading-relaxed">
                    Nach „Berechtigung erteilen“ einmal „Testen“ tippen. Danach kommen neue Mails
                    auch ohne offenen Tab. Windows: System → Benachrichtigungen → Browser an.
                    iPhone nur als zum Home-Bildschirm hinzugefügte App.
                  </p>
                )}
              </div>

              <div className="py-5 space-y-4">
                <div className="flex items-center gap-2 text-sm font-medium text-neutral-900 dark:text-neutral-100">
                  <CalendarClock className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                  Termin-Erinnerungen
                </div>
                <SettingsToggle
                  checked={!!mailPrefs.calendarReminders}
                  onChange={(calendarReminders) => updateMailPrefs({ calendarReminders })}
                  label="Erinnerungen vor Terminen"
                  description="Desktop-Toast und Hinweis, bevor ein Kalendertermin beginnt. Der Browser muss geöffnet sein."
                />
                <div
                  className={`space-y-2 ${!mailPrefs.calendarReminders ? 'opacity-50 pointer-events-none' : ''}`}
                >
                  <p className="text-xs text-neutral-500 dark:text-neutral-400">
                    Wann vor dem Termin erinnern? Mehrere Zeiten möglich.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {CALENDAR_REMINDER_OPTIONS.map((opt) => {
                      const selected = (mailPrefs.calendarReminderMinutes || []).includes(opt.value);
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => {
                            const current = Array.isArray(mailPrefs.calendarReminderMinutes)
                              ? mailPrefs.calendarReminderMinutes
                              : [];
                            let next;
                            if (selected) {
                              next = current.filter((m) => m !== opt.value);
                              if (!next.length) next = [15];
                            } else {
                              next = [...current, opt.value].sort((a, b) => a - b);
                            }
                            updateMailPrefs({ calendarReminderMinutes: next });
                          }}
                          className={`inline-flex items-center rounded-full px-3 py-1.5 text-xs font-medium border transition-colors ${
                            selected
                              ? 'border-primary-500 bg-primary-50 text-primary-800 dark:bg-primary-950/40 dark:text-primary-200 dark:border-primary-500'
                              : 'border-neutral-300 dark:border-neutral-600 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800'
                          }`}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="pt-5 space-y-4">
                <div className="flex items-center gap-2 text-sm font-medium text-neutral-900 dark:text-neutral-100">
                  <Eye className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                  Öffnungs-Tracking
                </div>
                <SettingsToggle
                  checked={!!mailPrefs.trackEmailOpens}
                  onChange={(trackEmailOpens) => updateMailPrefs({ trackEmailOpens })}
                  label="Öffnungen tracken"
                  description="Fügt ausgehenden Mails ein unsichtbares Tracking-Pixel hinzu. Mit Banner-Bild zählt das Banner als Tracker; sonst ein 1×1-Pixel."
                />

                {mailPrefs.trackEmailOpens && (
                  <div className="space-y-3">
                    {trackingInfo?.publiclyReachable ? (
                      <div className="rounded-xl border border-emerald-200 dark:border-emerald-900/50 bg-emerald-50/80 dark:bg-emerald-950/30 px-3.5 py-3">
                        <p className="text-sm font-medium text-emerald-800 dark:text-emerald-200">
                          Tracking ist aktiv und von außen erreichbar
                        </p>
                        <p className="mt-1 text-xs text-emerald-700/90 dark:text-emerald-300/80 leading-relaxed">
                          Beim Versand wird automatisch getrackt.
                          {trackingInfo?.resolvedBaseUrl ? (
                            <>
                              {' '}
                              Basis: <code className="text-[11px]">{trackingInfo.resolvedBaseUrl}</code>
                            </>
                          ) : null}
                        </p>
                        <p className="mt-2 text-[11px] text-emerald-700/80 dark:text-emerald-300/70 font-mono break-all">
                          Pixel: {trackingInfo.resolvedBaseUrl}/api/t/&lt;token&gt;.gif
                          <br />
                          Banner: {trackingInfo.resolvedBaseUrl}/api/t/&lt;token&gt;/banner
                          <br />
                          Statisch: {trackingInfo.resolvedBaseUrl}/pixel.gif
                        </p>
                      </div>
                    ) : (
                      <div className="rounded-xl border border-amber-200 dark:border-amber-900/40 bg-amber-50/80 dark:bg-amber-950/20 px-3.5 py-3">
                        <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
                          Noch nicht öffentlich erreichbar
                        </p>
                        <p className="mt-1 text-xs text-amber-800/90 dark:text-amber-300/80 leading-relaxed">
                          Aktuell: <code className="text-[11px]">{trackingInfo?.resolvedBaseUrl || '—'}</code>.
                          Setze unten eine öffentliche Server-URL (z.&nbsp;B. deinen vServer), sonst zählen Öffnungen bei Empfängern nicht.
                        </p>
                      </div>
                    )}

                    <details className="group">
                      <summary className="cursor-pointer text-xs font-medium text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-200">
                        Erweitert: eigene Tracking-URL
                      </summary>
                      <div className="mt-2 space-y-2">
                        <input
                          type="url"
                          value={mailPrefs.trackingBaseUrl || ''}
                          onChange={(e) =>
                            updateMailPrefs({ trackingBaseUrl: e.target.value }, { debounceMs: 500 })
                          }
                          className="input-field"
                          placeholder="https://dein-server.example.com"
                        />
                        <p className="text-xs text-neutral-500 dark:text-neutral-400 leading-relaxed">
                          Leer = automatisch aus Server-Umgebung (<code className="text-[11px]">PUBLIC_BASE_URL</code>).
                        </p>
                      </div>
                    </details>
                  </div>
                )}
              </div>
            </SettingsSection>
          )}

          {activeSection === 'mail' && (
            <SettingsSection
              icon={Reply}
              title="Mail-Verhalten"
              description="Rückgängig-Puffer und Antwort-Verhalten."
              bodyClassName="space-y-0 divide-y divide-neutral-200 dark:divide-neutral-700"
            >
              <div className="pb-5 space-y-3">
                <div className="flex items-center gap-2 text-sm font-medium text-neutral-900 dark:text-neutral-100">
                  <Timer className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                  Rückgängig-Puffer
                </div>
                <p className="text-xs text-neutral-500 dark:text-neutral-400">
                  Zeitfenster, in dem du den Versand abbrechen oder eine Löschung rückgängig machen kannst.
                </p>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                  Wartezeit in Sekunden
                </label>
                <div className="flex items-center gap-3 max-w-sm">
                  <input
                    type="number"
                    min={0}
                    max={60}
                    step={1}
                    value={mailPrefs.undoDelaySeconds ?? 10}
                    onChange={(e) => {
                      const n = parseInt(e.target.value, 10);
                      updateMailPrefs(
                        { undoDelaySeconds: Number.isFinite(n) ? Math.min(60, Math.max(0, n)) : 10 },
                        { debounceMs: 400 }
                      );
                    }}
                    className="input-field w-28"
                  />
                  <span className="text-sm text-neutral-500 dark:text-neutral-400">0–60 (0 = sofort)</span>
                </div>
                <p className="text-xs text-neutral-500 dark:text-neutral-400">
                  Standard: 10 Sekunden. Gilt für „Senden rückgängig“ und „Löschen rückgängig“.
                </p>
              </div>

              <div className="pt-5 space-y-4">
                <div className="flex items-center gap-2 text-sm font-medium text-neutral-900 dark:text-neutral-100">
                  <Reply className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                  Antwort-Verhalten
                </div>
                <SettingsToggle
                  checked={!!mailPrefs.replyQuoteOriginal}
                  onChange={(replyQuoteOriginal) => updateMailPrefs({ replyQuoteOriginal })}
                  label="Originalnachricht anhängen"
                  description="Beim Antworten wird die Original-Mail unter Ihrer Antwort zitiert."
                />
                <SettingsToggle
                  checked={mailPrefs.showInboxSentHistory !== false}
                  onChange={(showInboxSentHistory) => updateMailPrefs({ showInboxSentHistory })}
                  label="Gesendete-Mails-Verlauf in der Inbox"
                  description="Unter empfangenen Mails den Verlauf anzeigen, was Sie diesem Absender bereits geschickt haben. Auch direkt in der Mail per Schalter umschaltbar."
                />
              </div>
            </SettingsSection>
          )}

          {activeSection === 'spam' && <SpamRulesSettings embedded />}

          {activeSection === 'subscriptions' && <SubscriptionSettings embedded />}

          {activeSection === 'automation' && (
            <SettingsSection
              icon={Bot}
              title="Automatische Antworten & Weiterleitung"
              description="Wird bei neuen eingehenden Mails geprüft (ca. alle 5 Minuten). Änderungen werden automatisch gespeichert."
            >
              <div className="settings-subblock">
                <SettingsToggle
                  checked={!!mailPrefs.firstContactAutoReply.enabled}
                  onChange={(enabled) => updateNestedMailPrefs('firstContactAutoReply', { enabled })}
                  label="Erstkontakt-Antwort"
                  description="Automatische Antwort, wenn jemand schreibt und wir dieser Adresse noch nie eine E-Mail geschickt haben. Wird bei aktiver Abwesenheit übersprungen."
                />
                <div
                  className={`space-y-3 ${
                    !mailPrefs.firstContactAutoReply.enabled ? 'opacity-50 pointer-events-none' : ''
                  }`}
                >
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                      Betreff (optional)
                    </label>
                    <input
                      type="text"
                      className="input-field"
                      value={mailPrefs.firstContactAutoReply.subject}
                      onChange={(e) =>
                        updateNestedMailPrefs(
                          'firstContactAutoReply',
                          { subject: e.target.value },
                          { debounceMs: 450 }
                        )
                      }
                      placeholder="Leer = Re: Originalbetreff"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                      Nachricht
                    </label>
                    <textarea
                      className="input-field resize-y"
                      rows={4}
                      value={mailPrefs.firstContactAutoReply.message}
                      onChange={(e) =>
                        updateNestedMailPrefs(
                          'firstContactAutoReply',
                          { message: e.target.value },
                          { debounceMs: 450 }
                        )
                      }
                      placeholder="Vielen Dank für Ihre Nachricht. Wir melden uns in Kürze…"
                    />
                  </div>
                </div>
              </div>

              <div className="settings-subblock">
                <SettingsToggle
                  checked={!!mailPrefs.vacation.enabled}
                  onChange={(enabled) => updateNestedMailPrefs('vacation', { enabled })}
                  label={
                    <span className="inline-flex items-center gap-2">
                      <Plane className="w-4 h-4 text-neutral-400" />
                      Abwesenheitsnotiz
                    </span>
                  }
                  description="Automatische Inaktivitäts-Nachricht. Pro Absender maximal einmal pro Tag."
                />
                <div className={`space-y-3 ${!mailPrefs.vacation.enabled ? 'opacity-50 pointer-events-none' : ''}`}>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                      Abwesend bis
                    </label>
                    <div className="flex flex-wrap items-center gap-2">
                      <input
                        type="date"
                        className="input-field max-w-[12rem]"
                        value={mailPrefs.vacation.until || ''}
                        min={new Date().toISOString().slice(0, 10)}
                        onChange={(e) =>
                          updateNestedMailPrefs('vacation', { until: e.target.value }, { debounceMs: 300 })
                        }
                      />
                      {mailPrefs.vacation.until ? (
                        <button
                          type="button"
                          className="text-xs font-medium text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200"
                          onClick={() => updateNestedMailPrefs('vacation', { until: '' })}
                        >
                          Datum entfernen
                        </button>
                      ) : null}
                    </div>
                    <p className="mt-1.5 text-[11px] text-neutral-500 dark:text-neutral-400 leading-snug">
                      Nach diesem Tag werden Abwesenheitsnotiz und automatische Weiterleitung
                      automatisch deaktiviert. Ohne Datum bleiben sie an, bis du sie ausschaltest.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                      Betreff
                    </label>
                    <input
                      type="text"
                      className="input-field"
                      value={mailPrefs.vacation.subject}
                      onChange={(e) =>
                        updateNestedMailPrefs('vacation', { subject: e.target.value }, { debounceMs: 450 })
                      }
                      placeholder="Abwesend / Out of Office"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                      Nachricht
                    </label>
                    <textarea
                      className="input-field resize-y"
                      rows={4}
                      value={mailPrefs.vacation.message}
                      onChange={(e) =>
                        updateNestedMailPrefs('vacation', { message: e.target.value }, { debounceMs: 450 })
                      }
                      placeholder="Ich bin derzeit nicht im Büro und melde mich nach meiner Rückkehr…"
                    />
                  </div>
                </div>
              </div>

              <div className="settings-subblock">
                <SettingsToggle
                  checked={!!mailPrefs.forward.enabled}
                  onChange={(enabled) => updateNestedMailPrefs('forward', { enabled })}
                  label={
                    <span className="inline-flex items-center gap-2">
                      <Forward className="w-4 h-4 text-neutral-400" />
                      Automatische Weiterleitung
                    </span>
                  }
                  description={
                    mailPrefs.vacation.enabled && mailPrefs.vacation.until
                      ? `Neue Mails weiterleiten. Wird am ${String(mailPrefs.vacation.until)
                          .split('-')
                          .reverse()
                          .join('.')} zusammen mit der Abwesenheit automatisch deaktiviert.`
                      : 'Neue eingehende Mails zusätzlich an eine andere Adresse weiterleiten.'
                  }
                />
                <div className={!mailPrefs.forward.enabled ? 'opacity-50 pointer-events-none' : ''}>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    Weiterleiten an
                  </label>
                  <input
                    type="email"
                    className="input-field"
                    value={mailPrefs.forward.to}
                    onChange={(e) =>
                      updateNestedMailPrefs('forward', { to: e.target.value }, { debounceMs: 450 })
                    }
                    placeholder="vertretung@firma.de"
                  />
                </div>
              </div>
            </SettingsSection>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;
